import {SECONDS_IN_HOUR} from '@wandb/weave/common/util/time';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import React, {FC, Fragment} from 'react';
// eslint-disable-next-line wandb/no-deprecated-imports
import {Icon} from 'semantic-ui-react';

import {createDismissibleBanner} from '../../../pages/Billing/AccountSettings/WarningBanner/DismissibleBanner';
import {Analytics} from '../../../services/analytics';
import {
  Banner,
  BannerBoldText,
  BannerFlexWrapper,
  BannerTextWrapper,
} from '../../Banner';
import {
  type GlobalBannerState,
  ReachingTeamsPlanLimitBannerProps,
} from '../types';
import {useGetFilteredGlobalBannerStates} from '../useFilteredGlobalBannerStates';
import {
  BillingAdminMonthlyCTAText,
  NonBillingAdminMonthlyCTAText,
} from './BannerText';

export const ReachingTeamsPlanLimitBanner: FC = () => {
  const states = useGetFilteredGlobalBannerStates();

  if (states == null) {
    return null;
  }

  return (
    <Tailwind>
      <div className={`mb-8 space-y-2`}>
        <ReachingTeamsPlanLimitBannerStack states={states} />
      </div>
    </Tailwind>
  );
};

const ReachingTeamsPlanLimitBannerStack: React.FC<{
  states: GlobalBannerState[];
}> = ({states}) => {
  return (
    <>
      {states.map(state => {
        return (
          <Fragment key={state.account.name + state.account.accountType}>
            {state.teamsPlanEnforcementBannerFlags
              .reachingTeamsPlanLimitBannerProps && (
              <ReachingTeamsPlanLimitBannerComp
                {...state.teamsPlanEnforcementBannerFlags
                  .reachingTeamsPlanLimitBannerProps}
              />
            )}
          </Fragment>
        );
      })}
    </>
  );
};

const ReachingTeamsPlanLimitBannerComp =
  createDismissibleBanner<ReachingTeamsPlanLimitBannerProps>({
    id: ({organizationId}) => {
      return organizationId + ':teams-plan-85percent';
    },
    Component: ({
      onDismiss,
      organizationName,
      computeSecondsLimit,
      computeSecondsUsed,
      organizationId,
      billingInterval,
      isBillingAdmin,
      billingAdminEmail,
    }) => {
      const remainingComputePercentageText = (
        1 -
        computeSecondsUsed / computeSecondsLimit
      ).toLocaleString('en-us', {style: 'percent', maximumFractionDigits: 0});
      const computeHoursLimitText = (
        computeSecondsLimit / SECONDS_IN_HOUR
      ).toLocaleString('en-us', {maximumFractionDigits: 0});
      return (
        <Banner
          variant={'warning'}
          eventData={{
            location: `exceeded 85% for teams plan limit alert banner`,
            organizationId,
          }}>
          <BannerFlexWrapper>
            <div className="text-xs">
              <Icon name="info" />
            </div>
            <BannerTextWrapper>
              <span>
                <BannerBoldText>
                  You only have {remainingComputePercentageText} of your{' '}
                  {computeHoursLimitText} {billingInterval} tracking hours
                  remaining.
                </BannerBoldText>{' '}
                Any tracking hours over that limit will be charged at $1 per
                tracking hour.{' '}
                {billingInterval === 'monthly' && (
                  <>
                    {isBillingAdmin || billingAdminEmail == null ? (
                      <BillingAdminMonthlyCTAText
                        organizationName={organizationName}
                      />
                    ) : (
                      <NonBillingAdminMonthlyCTAText
                        billingAdminEmail={billingAdminEmail}
                      />
                    )}{' '}
                  </>
                )}
              </span>
            </BannerTextWrapper>
          </BannerFlexWrapper>
          <button
            className="flex items-center border-none bg-transparent font-semibold text-sienna-600 hover:cursor-pointer hover:text-sienna-500"
            onClick={() => {
              Analytics.track('Dismiss button clicked', {
                location: `exceeded 85% for teams plan limit alert banner`,
                organizationId,
              });
              onDismiss();
            }}>
            Dismiss
          </button>
        </Banner>
      );
    },
  });
